import { Popover, Spin } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { STATE_ABBREVIATIONS } from 'constants/stateAbbreviations';
import { useMembersForPod } from 'utils/hooks/pods/useMembersForPod';

import { useGetPod } from '../../hooks/use-pod-management';
import s from './styles.module.scss';

import type { FC, KeyboardEvent, ReactNode } from 'react';
import type { PodClinic, PodMember } from '../../types';

interface IPodCellPopoverProps {
  podGuid: string;
  type: 'clinics' | 'members';
  children: ReactNode;
}

// Reverse map: abbreviation → full name (e.g. "PA" → "Pennsylvania")
const ABBREVIATION_TO_STATE: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_ABBREVIATIONS).map(([full, abbr]) => [abbr, full]),
);

/** Resolve a state value that may be a full name, abbreviation, or "none"/null. */
const resolveStateName = (state: string | null): string | null => {
  if (!state || state.toLowerCase() === 'none') return null;
  // Already a full name (key exists in STATE_ABBREVIATIONS)
  if (STATE_ABBREVIATIONS[state]) return state;
  // Try abbreviation lookup
  return ABBREVIATION_TO_STATE[state.toUpperCase()] ?? state;
};

// Record<string, string> intentional — gracefully handles unknown priority values via ?? '' fallback
const PRIORITY_LABEL_KEYS: Record<string, string> = {
  primary: 'labels.primary',
  secondary: 'labels.secondary',
  tertiary: 'labels.tertiary',
};

const PodCellPopover: FC<IPodCellPopoverProps> = ({
  podGuid,
  type,
  children,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);

  // Lazy fetch: only fires after popover has been opened at least once.
  // React Query caches by [API_PODS, 'detail', podGuid] — subsequent
  // opens (or Edit modal) reuse the cache.
  // Single guard: `enabled: hasOpened` controls when the query fires.
  // `!!guid` inside useGetPod serves as a safety net for empty/null podGuid props.
  const { clinics, members, isLoading, isError } = useGetPod(podGuid, {
    enabled: hasOpened,
  });

  // Pod detail API may return role: null for standard roles. The member
  // list API (useMembersForPod) derives roles from the profession field
  // — use it as a fallback. React Query caching ensures no duplicate requests.
  const { members: allMembers } = useMembersForPod();
  const memberRoleLookup = useMemo(
    () => new Map(allMembers.map((m) => [m.loginGuid, m.role])),
    [allMembers],
  );

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen && !hasOpened) setHasOpened(true);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      // preventDefault stops Enter from synthesizing a click event,
      // which would double-trigger onOpenChange via Ant Popover's click handler.
      e.preventDefault();
      handleOpenChange(true);
    }
    if (e.key === 'Escape' && open) {
      e.preventDefault();
      // Unlike Actions button (R4-3 in Story 4.3), Escape propagation to a
      // future modal would close the modal unintentionally — defensive
      // stopPropagation is justified for keyboard UX safety.
      e.stopPropagation();
      handleOpenChange(false);
    }
  };

  const renderClinicContent = (items: PodClinic[]) => {
    if (!items.length) return <span className={s.emptyText}>—</span>;
    return items.map((clinic) => {
      const stateName = resolveStateName(clinic.state);
      return (
        <div key={clinic.guid} className={s.popoverRow}>
          <span className={s.popoverName}>{clinic.name}</span>
          {stateName && <span className={s.popoverMeta}>({stateName})</span>}
        </div>
      );
    });
  };

  const renderMemberContent = (items: PodMember[]) => {
    if (!items.length) return <span className={s.emptyText}>—</span>;
    return items.map((member) => {
      const role =
        member.role || memberRoleLookup.get(member.loginGuid) || null;
      const priorityLabel =
        member.priority && PRIORITY_LABEL_KEYS[member.priority.toLowerCase()]
          ? t(PRIORITY_LABEL_KEYS[member.priority.toLowerCase()], '')
          : member.priority ?? null;
      return (
        <div key={member.loginGuid} className={s.popoverRow}>
          <span className={s.popoverName}>
            {member.name}
            {role && <span className={s.popoverRole}> - {role}</span>}
          </span>
          {priorityLabel && (
            <span className={s.popoverMeta}>({priorityLabel})</span>
          )}
        </div>
      );
    });
  };

  const popoverContent = () => {
    if (isLoading) {
      return (
        <div className={s.popoverLoading}>
          <Spin size="small" />
        </div>
      );
    }

    if (isError) {
      return <span className={s.emptyText}>{t('labels.unableToLoad')}</span>;
    }

    const items = type === 'clinics' ? clinics : members;
    if (!items?.length) return <span className={s.emptyText}>—</span>;

    return (
      <div className={s.popoverContent}>
        {type === 'clinics'
          ? renderClinicContent(items as PodClinic[])
          : renderMemberContent(items as PodMember[])}
      </div>
    );
  };

  return (
    <Popover
      content={popoverContent}
      open={open}
      onOpenChange={handleOpenChange}
      arrow={false}
      placement="bottom"
      overlayClassName={s.popoverOverlay}
    >
      <span
        tabIndex={0}
        onKeyDown={handleKeyDown}
        className={s.popoverTrigger}
        role="button"
        aria-haspopup="true"
        aria-expanded={open}
        data-cy={`pod-list__${type}-popover-trigger`}
      >
        {children}
      </span>
    </Popover>
  );
};

export default PodCellPopover;
